import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Flex,
  Modal,
  Table,
  Typography,
  Form,
  Select,
  Input,
  message,
  Space,
  Popover,
  Checkbox,
  Divider,
} from "antd";
import { useGetUsimOrdersByStatusMutation, useUpdateUsimOrderStatusMutation } from "../../../../api/usim/orders/UsimOrdersApi";
import OrderDetailModal from "../../shared/orderDetailModal";
import { UsimOrderItemData } from "../../../../api/usim/orders/UsimOrderItem";
import { getCommonColumns, groupOrdersByOrderNumber, searchOrders, exportToCSV } from "../../shared/tableUtils";
import { SettingOutlined, ExportOutlined, EditOutlined, AppstoreOutlined } from "@ant-design/icons";
import EditRetailorderPage from "../../shared/editOrderPage";
import { formatDateISO } from "../../../../utils/date";
import * as XLSX from "xlsx";

import { USimStatus, USimStatusLabel, fromUSimStatus, toUSimStatus, USimStatusOptions } from "../../shared/usimUtils";

const { Title } = Typography;

// Define relevant status options for filtering
const ORDER_STATUS_OPTIONS = [
  { label: USimStatusLabel[USimStatus.PENDING_PROCESSING], value: USimStatus.PENDING_PROCESSING },
];

const BASE_COLUMNS = ["orderProcessedDate", "OrderNumber", "type", "sku", "iccid", "mobileNumber", "status", "activationDate", "edit", "action"];
const COLUMNS_STORAGE_KEY = "usimPendingActivationTableColumns";

export default function UsimManageOrderPage() {
  const [tableData, setTableData] = useState<UsimOrderItemData[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<UsimOrderItemData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<string>(USimStatus.COMPLETE);
  const [updateDescription, setUpdateDescription] = useState<string>("");
  const [updateActivationDate, setUpdateActivationDate] = useState<any>(null);
  const [triggerUpdateStatus, { isLoading: isUpdating }] = useUpdateUsimOrderStatusMutation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [orderEditRecord, setEditOrderRecord] = useState<any>();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, string[]>>({});

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : BASE_COLUMNS;
    } catch (e) {
      return BASE_COLUMNS;
    }
  });

  const handleColumnVisibilityChange = (checkedValues: any[]) => {
    setVisibleColumns(checkedValues as string[]);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(checkedValues));
  };

  const resetColumns = () => {
    setVisibleColumns(BASE_COLUMNS);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(BASE_COLUMNS));
  };

  const showDetailsModal = (order: UsimOrderItemData) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Get common columns and add action column
  const allColumns = [
   {
       title: "Order Processed Date",
       dataIndex: "orderProcessedDate",
       key: "orderProcessedDate",
       tag: "all",
       render: (text: string, record: UsimOrderItemData) => 
         record.orderProcessedDate ? formatDateISO(record.orderProcessedDate) : "-",
       onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
       sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => a.orderProcessedDate.localeCompare(b.orderProcessedDate)
     },
     {
       title: "Order Number",
       dataIndex: "OrderNumber",
       key: "OrderNumber",
       tag: "all",
       onCell: (record: UsimOrderItemData) => ({ rowSpan: record._rowSpan ?? 1 }),
       render: (text: string, record: UsimOrderItemData) => 
         record.status?.toUpperCase() === 'CANCELLED' ? (
           `<div style={{ backgroundColor: 'red', color: 'white', padding: '4px', borderRadius: '4px' }}>
          {record.OrderNumber || "-"}
           </div>
         `) : record.OrderNumber || "-"
     },
     {
       title: "Type",
       dataIndex: "type",
       key: "type",
       tag: "all",
       filters: Array.from(new Set(tableData.map(item => item.type)))
        .filter(Boolean)
        .map(type => ({
          text: type,
          value: type,
        })),
      onFilter: (value: string, record: UsimOrderItemData) => 
        record.type === value,
      filteredValue: filteredInfo.type || null,
     },
     {
       title: "SKU",
       dataIndex: "sku",
       key: "sku",
       tag: "all",
     },
     {
       title: "ICCID/SN",
       dataIndex: "iccid",
       key: "iccid",
       tag: "all",
     },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
     {
       title: "Status",
       dataIndex: "status",
       key: "status",
       tag: "all",
       render: (text: string, record: UsimOrderItemData) => fromUSimStatus(record.status),
       filters: ORDER_STATUS_OPTIONS.map(({ label, value }) => ({
        text: label,
        value: value,
      })),
      onFilter: (value: string, record: UsimOrderItemData) => 
        toUSimStatus(record.status) === value,
      filteredValue: filteredInfo.status || null,
     },
     {
       title: "Activation Date",
       dataIndex: "activationDate",
       key: "activationDate",
       tag: "all",
       render: (text: string, record: UsimOrderItemData) => 
         record.activationDate ? formatDateISO(record.activationDate) : "-"
     },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => text ? formatDateISO(text) : "-",
      sorter: (a: UsimOrderItemData, b: UsimOrderItemData) => (a.createdAt || "").localeCompare(b.createdAt || ""),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string, record: UsimOrderItemData) => (record.updatedAt || record.updated_at) ? formatDateISO(record.updatedAt || record.updated_at) : "-",
    },
    {
      title: "Shop ID",
      dataIndex: "shopId",
      key: "shopId",
    },
    {
      title: "Plan Name",
      dataIndex: "planName",
      key: "planName",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Activated Date",
      dataIndex: "activatedDate",
      key: "activatedDate",
      render: (text: string) => text ? formatDateISO(text) : "-",
    },
    {
      title: "Processed By",
      dataIndex: "processedBy",
      key: "processedBy",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
    },
    {
      title: "Edit",
      key: "edit",
     render: (_: any, record: UsimOrderItemData) => (
      
         <Button
                    type="primary"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                     handleEditOrder(record)
                    }}
                  />
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: UsimOrderItemData) => (
        <Button onClick={() => showDetailsModal(record)}>
          View Details
        </Button>
      )
    }
  ];

  const columns = allColumns.filter(col => visibleColumns.includes(col.key as string));

  const columnOptions = allColumns.map(col => ({
    label: col.title as string,
    value: col.key as string,
  }));
    // handle the editing part
  const handleEditOrder = (record: UsimOrderItemData) => {
    setEditOrderRecord(record);
    setShowEditModal(true);
  };

  const [getOrdersByStatus, { data: orders = { items: [], count: 0, status_filters: [] }, error, isLoading }] = useGetUsimOrdersByStatusMutation();

  const loadOrders = async () => {
    try {
      const result = await getOrdersByStatus({
        status_list: [USimStatus.PENDING_PROCESSING]
      }).unwrap();
      if (Array.isArray(result?.items)) {
        const groupedData = groupOrdersByOrderNumber(result.items);
        setTableData(groupedData);
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      message.error("Failed to fetch orders");
      setTableData([]);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  function updateStatusModal() {
    setIsUpdateModalVisible(true);
    if (selectedOrder) {
      setUpdateActivationDate(selectedOrder.activationDate);
    }
  }

  function handleStatusUpdate() {
    const selectedOrders = tableData.filter(item => selectedRowKeys.includes(item.OrderNumber + item.shopId));
    if (!selectedOrders.length) {
      message.warning("Please select at least one order");
      return;
    }

    Promise.all(
      selectedOrders.map(item =>
        triggerUpdateStatus({
          id: item.OrderNumber,
          shopId: item.shopId ?? "1#1",
          status: toUSimStatus(updateStatus),
          activationDate: updateActivationDate || "",
          processedBy: updateDescription || "",
          comment: updateDescription || ""
        }).unwrap()
      )
    )
      .then(() => {
        message.success("Status updated successfully");
        setIsUpdateModalVisible(false);
        setSelectedRowKeys([]);
        setUpdateDescription("");
        loadOrders();
      })
      .catch(() => {
        message.error("Failed to update status");
      });
  }

    const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditOrderRecord(null);
    // refetch(); // Refresh the table data after editing
  };

  const handleExport = () => {
    try {
      // Filter data based on current filters
      let dataToExport = searchOrders(tableData, searchQuery);
      
      // Apply column filters if any
      if (filteredInfo.type?.length) {
        dataToExport = dataToExport.filter(item => filteredInfo.type.includes(item.type));
      }
      
      if (filteredInfo.status?.length) {
        dataToExport = dataToExport.filter(item => filteredInfo.status.includes(item.status));
      }
      
      // Format data for Excel export
      const formattedData = dataToExport.map(item => ({
        "Order Number": item.OrderNumber || "",
        "Order Processed Date": item.orderProcessedDate ? formatDateISO(item.orderProcessedDate) : "",
        "Type": item.type || "",
        "SKU": item.sku || "",
        "ICCID": item.iccid || "", // No prefix, will format as text in Excel
        "Status": fromUSimStatus(item.status) || "",
        "Activation Date": item.activationDate ? formatDateISO(item.activationDate) : "",
        "Mobile Number": item.mobileNumber || "", // No prefix, will format as text in Excel
        "Shop ID": item.shopId || "",
        "Created At": item.createdAt ? formatDateISO(item.createdAt) : "",
        "Updated At": item.updatedAt ? formatDateISO(item.updatedAt) : "",
      }));
      
      // Create a new workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      
      // Apply text format to ICCID column
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "");
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: 4 }); // Column 4 is ICCID
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].z = "@"; // Set format as text
      }
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
      
      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.href = url;
      link.download = `orders-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.style.visibility = "hidden";
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`Exported ${formattedData.length} orders successfully as Excel file`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export data. Please try again.");
    }
  };

  const optionsMenuItems = [
    {
      key: "export",
      label: "Export",
      icon: <ExportOutlined />,
      onClick: handleExport,
    }
  ];

  const processedData = searchOrders(tableData, searchQuery);

  return (
    <Flex gap={16} vertical style={{ padding: "24px" }}>
      <Title level={2}>Pending Activation Order</Title>
      <Flex justify="space-between" align="center">
        <Space>
          <Button type="primary" onClick={loadOrders}>
            Reload
          </Button>
          {selectedRowKeys.length > 0 && (
            <Button onClick={() => updateStatusModal()} type="primary">
              Update Status
            </Button>
          )}
        </Space>
        <Space>
          <Input.Search
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search all fields"
            style={{ width: 300 }}
          />
          <Dropdown menu={{ items: optionsMenuItems }} trigger={['hover']}>
            <Button icon={<SettingOutlined />}>
              Options
            </Button>
          </Dropdown>
          <Popover
            content={
              <div style={{ maxWidth: 300 }}>
                <Checkbox.Group
                  options={columnOptions}
                  value={visibleColumns}
                  onChange={handleColumnVisibilityChange}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}
                />
                <Divider style={{ margin: '12px 0' }} />
                <Button type="link" onClick={resetColumns} style={{ padding: 0 }}>
                  Reset to Default
                </Button>
              </div>
            }
            title="Visible Columns"
            trigger="click"
            placement="bottomRight"
          >
            <Button icon={<AppstoreOutlined />}>
              Columns
            </Button>
          </Popover>
        </Space>
      </Flex>

      {error && (
        <Alert
          message="Error"
          description="Failed to fetch orders. Please try again."
          type="error"
          showIcon
        />
      )}

      <Table
        rowKey={(record) => record.OrderNumber + record.shopId}
        columns={columns as any}
        dataSource={processedData}
        loading={isLoading}
        rowSelection={rowSelection}
        onChange={(pagination, filters) => {
          setFilteredInfo(filters as Record<string, string[]>);
        }}
        scroll={{ x: 'max-content' }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} orders`
        }}
      />

      {selectedOrder && (
        <OrderDetailModal
          isVisible={isModalVisible}
          orderData={selectedOrder}
          onClose={handleModalClose}
        />
      )}

      <Modal
        title="Update Status"
        open={isUpdateModalVisible}
        onOk={handleStatusUpdate}
        onCancel={() => setIsUpdateModalVisible(false)}
        confirmLoading={isUpdating}
      >
        <Form layout="vertical">
          <Form.Item label="Status">
            <Select
              value={updateStatus}
              onChange={(value) => setUpdateStatus(value)}
              options={USimStatusOptions}
            />
          </Form.Item>
          <Form.Item label="Activation Date">
            <DatePicker
              value={updateActivationDate}
              onChange={(date) => setUpdateActivationDate(date)}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Description">
            <Input.TextArea
              value={updateDescription}
              onChange={(e) => setUpdateDescription(e.target.value)}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    <Modal
            title="Edit Order"
            open={showEditModal}
            onCancel={handleEditModalClose}
            footer={null}
            width={1200}
            style={{ 
              top: 20,
              paddingBottom: 0
            }}
            styles={{
              body: {
                height: 'calc(100vh - 120px)',
                overflow: 'auto'
              }
            }}
          >
            {orderEditRecord && (
              <EditRetailorderPage
                orderNumber={orderEditRecord.OrderNumber}
                shopId={orderEditRecord.ShopId}
              />
            )}
          </Modal>
    </Flex>
  );
}
